import React from 'react';
import Navbar from '../Navbar';
import Footer from '../Footer';
import SEO from '../SEO';
import { Shield } from 'lucide-react';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="Política de Privacidad"
        description="Política de privacidad de asisant. Cómo recopilamos, usamos y protegemos tus datos personales conforme al RGPD."
        canonical="/privacidad"
      />
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-dark-ellipse">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 mb-6">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Política de Privacidad
          </h1>
          <p className="text-white/80">
            Última actualización: Enero 2025
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Introducción</h2>
            <p className="text-[#c3c3c5] mb-6">
              En Asisant, nos comprometemos a proteger la privacidad de nuestros usuarios. Esta Política de Privacidad describe cómo recopilamos, utilizamos, almacenamos y protegemos su información personal cuando utiliza nuestra plataforma de comercio cloud.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Información que Recopilamos</h2>
            <p className="text-[#c3c3c5] mb-4">Podemos recopilar los siguientes tipos de información:</p>
            <ul className="list-disc pl-6 text-[#c3c3c5] space-y-2 mb-6">
              <li><strong>Información de identificación personal:</strong> nombre, dirección de correo electrónico, número de teléfono, nombre de la empresa, cargo.</li>
              <li><strong>Información de facturación:</strong> dirección de facturación, información fiscal.</li>
              <li><strong>Información de uso:</strong> datos sobre cómo interactúa con nuestra plataforma, incluyendo páginas visitadas, funciones utilizadas y tiempo de sesión.</li>
              <li><strong>Información técnica:</strong> dirección IP, tipo de navegador, sistema operativo, identificadores de dispositivo.</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Cómo Utilizamos su Información</h2>
            <p className="text-[#c3c3c5] mb-4">Utilizamos la información recopilada para:</p>
            <ul className="list-disc pl-6 text-[#c3c3c5] space-y-2 mb-6">
              <li>Proporcionar y mantener nuestros servicios</li>
              <li>Procesar transacciones y gestionar suscripciones</li>
              <li>Enviar comunicaciones relacionadas con el servicio</li>
              <li>Mejorar y personalizar la experiencia del usuario</li>
              <li>Cumplir con obligaciones legales y regulatorias</li>
              <li>Proteger contra actividades fraudulentas</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Compartir Información</h2>
            <p className="text-[#c3c3c5] mb-6">
              No vendemos ni alquilamos su información personal a terceros. Podemos compartir información con proveedores de servicios de confianza que nos ayudan a operar nuestra plataforma, siempre bajo estrictos acuerdos de confidencialidad.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Seguridad de los Datos</h2>
            <p className="text-[#c3c3c5] mb-6">
              Implementamos medidas de seguridad técnicas y organizativas apropiadas para proteger su información personal contra acceso no autorizado, alteración, divulgación o destrucción. Esto incluye cifrado de datos, controles de acceso y auditorías de seguridad regulares.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Retención de Datos</h2>
            <p className="text-[#c3c3c5] mb-6">
              Conservamos su información personal durante el tiempo necesario para cumplir con los fines descritos en esta política, a menos que la ley exija o permita un período de retención más largo.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Sus Derechos</h2>
            <p className="text-[#c3c3c5] mb-4">Usted tiene derecho a:</p>
            <ul className="list-disc pl-6 text-[#c3c3c5] space-y-2 mb-6">
              <li>Acceder a su información personal</li>
              <li>Rectificar datos inexactos</li>
              <li>Solicitar la eliminación de sus datos</li>
              <li>Oponerse al procesamiento de sus datos</li>
              <li>Solicitar la portabilidad de sus datos</li>
              <li>Retirar su consentimiento en cualquier momento</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Cookies</h2>
            <p className="text-[#c3c3c5] mb-6">
              Utilizamos cookies y tecnologías similares para mejorar su experiencia en nuestra plataforma. Puede configurar su navegador para rechazar cookies, aunque esto puede afectar algunas funcionalidades del servicio.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Cambios en esta Política</h2>
            <p className="text-[#c3c3c5] mb-6">
              Podemos actualizar esta Política de Privacidad periódicamente. Le notificaremos cualquier cambio material publicando la nueva política en esta página y actualizando la fecha de "última actualización".
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Contacto</h2>
            <p className="text-[#c3c3c5] mb-6">
              Si tiene preguntas sobre esta Política de Privacidad o sobre cómo manejamos su información personal, puede contactarnos en:
            </p>
            <div className="bg-[#e3e4e6]/30 rounded-2xl p-6">
              <p className="text-gray-900 font-medium">Asisant</p>
              <p className="text-[#1297fd]">info@asisant.com</p>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
